<script lang="ts">

  import { Button } from "$lib/components/ui/button"
  import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
  } from "$lib/components/ui/dialog"
  import { FieldLabel, FieldMessage } from "$lib/components/ui/field"
  import { Input } from "$lib/components/ui/input"
  import { Textarea } from "$lib/components/ui/textarea"
  import FileSearchIcon from "@lucide/svelte/icons/file-search"
  import LoaderCircleIcon from "@lucide/svelte/icons/loader-circle"
  import Trash2Icon from "@lucide/svelte/icons/trash-2"
  import { createForm } from "@tanstack/svelte-form"
  import { createMutation, createQuery } from "@tanstack/svelte-query"
  import { toast } from "svelte-sonner"
  import { ulid } from "ulid"
  import { FormDocumentEmpty, type FormDocument } from "../_types"

  let {
    open,
    setOpen,
    select,
    onsave,
    onremove,
  }: {
    open: boolean
    setOpen: (open: boolean) => void
    select: FormDocument
    onsave: (document: FormDocument) => void
    onremove: (document: FormDocument) => void
  } = $props()

  const form = createForm(() => ({
    defaultValues: FormDocumentEmpty,
  }))

  createQuery(() => ({
    enabled: open,
    queryKey: ["customer-noo-document", select.ref_id, open],
    queryFn: async () => {

      form.reset(FormDocumentEmpty)
      form.setFieldValue("id", select.id)
      form.setFieldValue("ref_id", select.ref_id)
      form.setFieldValue("name", select.name)
      form.setFieldValue("description", select.description)
      form.setFieldValue("document", select.document)
      form.setFieldValue("document_url", select.document_url)
      form.setFieldValue("document_delete", select.document_delete)

      return ""
    },
  }))

  const mutation = createMutation(() => ({
    mutationFn: async (data: FormDocument) => {
      const document = {
        ...data,
        ref_id: data.ref_id || ulid(),
      }
      onsave(document)

      return document
    },
    onSuccess: () => {
      setOpen(false)
    },
    onError: (error: Error) => {
      toast.error("Terjadi kesalahan", {
        description: error.message,
      })
    },
  }))

  function preview() {
    const file = form.getFieldValue("document")
    const url = form.getFieldValue("document_url")

    if (file) {
      URL.createObjectURL(file).fileOpen("image")
      return
    }

    if (url) url.fileOpen("image")
  }

  function removeDocument() {
    onremove(form.state.values)
    setOpen(false)
  }

</script>

<Dialog
  open={open}
  onOpenChange={setOpen}>
  <DialogContent
    class="relative w-sm"
    onOpenAutoFocus={(event) => event.preventDefault()}>
    <DialogHeader>
      <DialogTitle>Dokumen</DialogTitle>
      <DialogDescription></DialogDescription>
    </DialogHeader>
    <form
      action="#"
      autocomplete="off"
      class="min-w-0 space-y-6"
      onsubmit={(event) => {
        event.preventDefault()
        mutation.mutate(form.state.values)
      }}>
      <div class="space-y-3">
        <form.Field name="name">
          {#snippet children(field)}
            <div>
              <FieldLabel>Judul</FieldLabel>
              <Input
                name={field.name}
                value={field.state.value}
                oninput={(event) => field.handleChange(event.currentTarget.value)}/>
              <FieldMessage
                name={field.name}
                form={form}/>
            </div>
          {/snippet}
        </form.Field>
        <form.Field name="description">
          {#snippet children(field)}
            <div>
              <FieldLabel>Keterangan</FieldLabel>
              <Textarea
                name={field.name}
                value={field.state.value}
                oninput={(event) => field.handleChange(event.currentTarget.value)}/>
              <FieldMessage
                name={field.name}
                form={form}/>
            </div>
          {/snippet}
        </form.Field>
        <form.Field name="document">
          {#snippet children(field)}
            <div>
              <FieldLabel>Dokumen</FieldLabel>
              <div class="flex gap-2">
                <Input
                  type="file"
                  name={field.name}
                  class="flex-1"
                  accept=".png, .jpg, .jpeg"
                  onchange={(event) => field.handleChange(event.currentTarget.files?.[0] ?? null)}/>
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  aria-label="Lihat dokumen"
                  disabled={!field.state.value && !form.getFieldValue("document_url")}
                  onclick={preview}>
                  <FileSearchIcon aria-hidden="true"/>
                </Button>
                <Button
                  type="button"
                  variant="destructive"
                  size="icon"
                  aria-label="Hapus dokumen"
                  disabled={!field.state.value && !form.getFieldValue("document_url")}
                  onclick={() => {
                    field.handleChange(null)
                    form.setFieldValue("document_url", "")
                    form.setFieldValue("document_delete", 1)
                  }}>
                  <Trash2Icon aria-hidden="true"/>
                </Button>
              </div>
              <FieldMessage
                name={field.name}
                form={form}/>
            </div>
          {/snippet}
        </form.Field>
      </div>
      <DialogFooter class="justify-between">
        <Button
          type="button"
          variant="destructive"
          disabled={!select.ref_id}
          onclick={removeDocument}>
          Hapus
        </Button>
        <div class="flex-1"></div>
        <Button
          type="button"
          variant="outline"
          disabled={mutation.isPending}
          onclick={() => setOpen(false)}>
          Tutup
        </Button>
        <Button
          type="submit"
          class="group"
          data-loading={mutation.isPending}
          disabled={mutation.isPending}>
          <LoaderCircleIcon
            class="invisible absolute animate-spin group-data-[loading=true]:visible"
            aria-hidden="true"/>
          <span class="group-data-[loading=true]:invisible">Simpan</span>
        </Button>
      </DialogFooter>
    </form>
  </DialogContent>
</Dialog>
